import { WorkflowCanvas } from "./workflowBuilderData";
import { PifIdentityMatch } from "./workflowRunData";
import {
  PIF_DETAILS_FIELDS,
  PIF_HR_PRE_APPROVAL,
  PIF_LOOKUP_BY,
  PIF_LOOKUP_FLOWS,
  PIF_START_FIELDS,
  PIF_VARIABLES,
  PifLookupBy,
} from "./pifWorkflowKeys";
import { WorkflowInstanceValueInputDto } from "../../types/workflowDto";

/** What pif-1 hands over on Continue. The typed SSN is never one of the case's answers
 *  (decisions log 4.1): `toPifStartValues` builds those from the fields below by name. */
export interface PifStartAnswers {
  match: PifIdentityMatch;
  lookupBy: PifLookupBy;
  /** The date of birth typed on pif-1, "YYYY-MM-DD" or "". */
  dateOfBirth: string;
  /** Last 4 digits of the looked-up SSN when the lookup was by SSN, else "". Saved masked. */
  ssnLast4: string;
  /**
   * Decision P4: the full SSN, 9 digits — a NEW HIRE looked up by SSN only, else undefined. It goes
   * to POST /workflow/pif/applicant once pif-1 is submitted and nowhere else: not into the case's
   * answers, not into a URL, toast or log.
   */
  newHireSsn?: string;
}

/**
 * pif-1's saved values, by field key, per the decisions log (section 2, 4.2):
 * - DOB: typed for a new hire, carried over from the lookup for a rehire, not stored for an
 *   existing employee.
 * - `pif.employeeId`: existing employee and rehire only.
 * - `pif.ssnLast4`: whenever the lookup used an SSN.
 * - `pif.applicantId` is deliberately absent: the server writes it (new hire only), and sending it
 *   from here, even blank, would overwrite that.
 */
export const toPifStartValues = (answers: PifStartAnswers): Record<string, string> => {
  const { match } = answers;
  const isExisting = match.flow === PIF_LOOKUP_FLOWS.existingActive;
  const isRehire = match.flow === PIF_LOOKUP_FLOWS.rehire;
  const dateOfBirth = isExisting ? "" : (isRehire && match.dateOfBirth) || answers.dateOfBirth;

  return {
    [PIF_START_FIELDS.hrPreApproval]: PIF_HR_PRE_APPROVAL.yes,
    [PIF_START_FIELDS.lookupBy]: answers.lookupBy,
    [PIF_START_FIELDS.dateOfBirth]: dateOfBirth,
    [PIF_VARIABLES.lookupFlow]: match.flow,
    [PIF_VARIABLES.employeeId]: isExisting || isRehire ? match.employeeId : "",
    [PIF_VARIABLES.ssnLast4]: answers.lookupBy === PIF_LOOKUP_BY.ssn ? answers.ssnLast4 : "",
  };
};

/** A case's saved values by field key, across every stage of its definition. */
export const toPifValuesByKey = (
  canvases: WorkflowCanvas[],
  values: Record<string, string>,
): Record<string, string> =>
  Object.fromEntries(
    canvases
      .flatMap((canvas) => canvas.fields)
      .filter((field) => field.fieldKey !== undefined && values[field.id] !== undefined)
      .map((field) => [field.fieldKey as string, values[field.id]]),
  );

const LOOKUP_FLOW_VALUES: string[] = Object.values(PIF_LOOKUP_FLOWS);

/**
 * The identity a resumed case was routed on, rebuilt from its saved values: what pif-1 saved, plus
 * the name and contact pif-3 has saved so far. Null when pif-1 saved no recognised flow — a case
 * with no route has no identity to show.
 */
export const fromPifSavedValues = (byKey: Record<string, string>): PifIdentityMatch | null => {
  const flow = byKey[PIF_VARIABLES.lookupFlow];
  if (!flow || !LOOKUP_FLOW_VALUES.includes(flow)) return null;
  const name = [PIF_DETAILS_FIELDS.firstName, PIF_DETAILS_FIELDS.middleName, PIF_DETAILS_FIELDS.lastName]
    .map((key) => byKey[key]?.trim())
    .filter(Boolean)
    .join(" ");
  return {
    flow: flow as PifIdentityMatch["flow"],
    name,
    employeeId: byKey[PIF_VARIABLES.employeeId] ?? "",
    dateOfBirth: byKey[PIF_START_FIELDS.dateOfBirth] ?? "",
    phone: byKey[PIF_DETAILS_FIELDS.cell] || byKey[PIF_DETAILS_FIELDS.homePhone] || undefined,
    email: byKey[PIF_DETAILS_FIELDS.email] || undefined,
  };
};

/** A patch by field key, as a patch by the stage's field ids (keys the stage lacks are dropped). */
export const toFieldIdPatch = (stage: WorkflowCanvas, byKey: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    stage.fields
      .filter((field) => field.fieldKey !== undefined && field.fieldKey in byKey)
      .map((field) => [field.id, byKey[field.fieldKey as string]]),
  );

/** Server field errors by field id, re-keyed by field key for the bespoke screens. */
export const toErrorsByKey = (stage: WorkflowCanvas, byId: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    stage.fields
      .filter((field) => field.fieldKey !== undefined && byId[field.id])
      .map((field) => [field.fieldKey as string, byId[field.id]]),
  );

/** The stage's fields that have a value in `byKey`, as save inputs. Fields with no key, or whose
 *  key is not in `byKey`, are left out, so their saved values are untouched. */
export const toKeyedValueInputs = (
  stage: WorkflowCanvas,
  byKey: Record<string, string>,
): WorkflowInstanceValueInputDto[] =>
  stage.fields
    .filter((field) => field.fieldKey !== undefined && field.fieldKey in byKey)
    .map((field) => ({ fieldId: Number(field.id), value: byKey[field.fieldKey as string] }));
